import { Ref, reactive, ref } from "vue";

import { Furikyuu, Jikangai, Kekkin, Kibiki, KoukyuuData, KyuukeiJikangai, Nittyoku, Yuukyuu } from "@/types/Shinsei";
import { ShinseiData, SoumuSyuuseiReq, SyuuseiData } from "@/types/SoumuSyuusei";
import { DakokuInfoShinseiForSoumuSyuusei,  } from "@/types/Dakoku";
import { DateUtil } from "@/utils/DateUtil";
import OptionConst from "@/const/Option";

export function useInit(syuuseiData: SyuuseiData | null) {
    const initSyuuseiData = () => {
        return <SyuuseiData>{
            before: {
                dakoku_id: 0,
                dakoku_date: null,
                gaisyutsu_datetime: null,
                gaisyutsu_round_datetime: null,
                koyou_keiyaku_id: 0,
                koyou_keiyaku_time_id: null,
                kyuukei_round_times: null,
                kyuukei_times: null,
                modori_datetime: null,
                modori_round_datetime: null,
                mst_syuugyou_basyo_id: 0,
                syukkin_datetime: null,
                syukkin_round_datetime: null,
                taikin_datetime: null,
                taikin_round_datetime: null,
                user_id: 0,
            },
            after: {
                dakoku_id: 0,
                dakoku_date: null,
                koyou_keiyaku_id: 0,
                koyou_keiyaku_time_id: null,
                mst_syuugyou_basyo_id: null,
                user_id: 0,
                gaisyutsu_time: null,
                modori_time: null,
                syukkin_time: null,
                taikin_time: null,
                kyuukei_time: null,
                syotei_work_times: null,
                koujo_times: null,
                houteinai_zangyou_times: null,
                jikangai_work_times: null,
                shinya_warimashi_times: null,
                chikoku_times: null,
                soutai_times: null,
            }
        }
    }

    const shinseiStatus: ShinseiData = reactive({
        isJikangai: false,
        isYuukyuu: false,
        isFurikyuu: false,
        isFuride: false,
        isNittyoku: false,
        isKekkin: false,
        isKibiki: false,
        isKyuukeiJikangai: false,
        isKoukyuu: false,
        jikangai: <Jikangai>{
            end_date: null,
            end_datetime: null,
            end_time: null,
            gyoumu_notes: null,
            iin_close_time: null,
            jikangai_date: null,
            start_date: null,
            start_datetime: null,
            start_time: null,
        },
        yuukyuu: <Yuukyuu>{
            start_date: null,
            time: null,
            yuukyuu_tani_type: null,
            yuukyuu_times: null,
        },
        furikyuu1: <Furikyuu>{
            furikyuu1_date: null,
            furikyuutani1_type: null,
        },
        furikyuu2: <Furikyuu>{
            furikyuu1_date: null,
            furikyuutani1_type: null,
        },
        furide: <Furikyuu>{
            end_time: null,
            kyuukei_time: null,
            start_time: null,
            syukkin_date: null,
            syukkin_furidetani_type: null,
        },
        nittyoku: <Nittyoku>{
            shinsei_date: null,
        },
        kekkin: <Kekkin>{
            start_date: null,
            end_date: null,
            kekkin_days: null,
        },
        kibiki: <Kibiki>{
            kibiki_start_date: null,
            kibiki_tani_type: null,
            kibiki_times: null,
            kojin_name: null,
            kojin_kana: null,
            tsuzukigara: null,
            sikyo_date: null,
            tsuya_date: null,
            kokubetsushiki_date: null,
            kaijou_name: null,
            kaijou_address: null,
            kaijou_tel: null,
        },
        kyuukeiJikangai: <KyuukeiJikangai>{
            kyuukei_jikangai_date: null,
            kyuukei_jikangai_times: null,
            gyoumu_notes: null,
        },
        koukyuu: <KoukyuuData>{
            koukyuu_date: null,
            koukyuu_tani_type: null,
            koyou_keiyaku_id: null,
            mst_syuugyou_basyo_id: null,
        },
        // 異動届の「半日補正」は不要（メモをやりとするためなので不要24.2.13 田中さん）
    })

    const form: Ref<SoumuSyuuseiReq> = ref({
        preDakokuId: null,
        shinsei: shinseiStatus,
        syuuseiData: initSyuuseiData().after
    });



    if (syuuseiData?.after) {
        form.value.syuuseiData = syuuseiData.after
    }

    const initShinseis = async (date: string | Date | null, shinseis: DakokuInfoShinseiForSoumuSyuusei | null) => {
        if (!shinseis || !date) {
            return
        }

        date = DateUtil.format(date, 'YYYY-MM-DD')
        // console.log("申請の初期化", date)
        // 以下、ある一つの申請種別について複数の申請がある場合は取消済みではない最後の要素を扱う（24.3.18 大塚）
        // ただしこれは仕様ではなくシステム上での暗黙的な動きなので支障があれば修正
        // 受け取った際の並び順によるが現在は指定なし（＝ID昇順）なので、
        // 最後の要素（＝最も最後に承認された申請）を正として扱う

        // 以下、基本的には以下のset◯◯◯系のメソッドと、
        // src/resources/ts/composable/kinmu/soumu-syuusei/syuusei.ts
        // 以下のテンプレートで変更されているフィールドの値をセットする
        // src/resources/ts/components/dakoku/SoumuSyuusei.vue

        // 本当はインターフェース含めメソッドも統一したいが時間が無いので・・・
        // 時間外
        const tmpJikangais = shinseis.jikangai.filter((item => item.syounin_status_type !== OptionConst.SYOUNIN_STATUS.CANCEL)) // 取消済み申請の除外
        const jikangai = tmpJikangais.at(-1)
        if (jikangai) {
            shinseiStatus.isJikangai = true
            shinseiStatus.jikangai.jikangai_date = jikangai.detail.jikangai_date
            shinseiStatus.jikangai.start_date = jikangai.detail.jikangai_date
            shinseiStatus.jikangai.end_date = jikangai.detail.jikangai_date
            shinseiStatus.jikangai.start_time = jikangai.detail.start_time
            shinseiStatus.jikangai.end_time = jikangai.detail.end_time
        }

        // 休憩未取得
        const tmpKyuukeiJikangai = shinseis.kyuukei.filter((item => item.syounin_status_type !== OptionConst.SYOUNIN_STATUS.CANCEL))
        const kyuukeiJikangai = tmpKyuukeiJikangai.at(-1)
        if (kyuukeiJikangai) {
            shinseiStatus.isKyuukeiJikangai = true
            shinseiStatus.kyuukeiJikangai.kyuukei_jikangai_date = kyuukeiJikangai.detail.kyuukei_jikangai_date
            shinseiStatus.kyuukeiJikangai.kyuukei_jikangai_times = kyuukeiJikangai.detail.kyuukei_jikangai_times
        }

        // 日直
        const tmpNittyoku = shinseis.nittyoku.filter((item => item.syounin_status_type !== OptionConst.SYOUNIN_STATUS.CANCEL))
        const nittyoku = tmpNittyoku.at(-1)
        if (nittyoku) {
            shinseiStatus.isNittyoku = true
            shinseiStatus.nittyoku.shinsei_date = nittyoku.detail.shinsei_date
        }

        // 振休・振出
        // 振休はダイアログ上は区分欄を２つ用意し、その２つに割り当てる
        // 以降shinseiStatus.furikyuu1とshinseiStatus.furikyuu2にそれぞれ割り当てる（それぞれにあるfurikyuu2_dateとfurikyuutani2_typeは使用しない）
        shinseis.furikyuu.forEach(item => {
            if (item.syounin_status_type === OptionConst.SYOUNIN_STATUS.CANCEL) {
                // 取り消し済みは除外
                return
            }

            if (shinseiStatus.furikyuu1.furikyuutani1_type !== null && shinseiStatus.furikyuu2.furikyuutani1_type !== null && shinseiStatus.furide.syukkin_furidetani_type !== null) {
                // ダイアログの振休の区分欄、振出いずれもすでにセット済みの場合は処理しない
                return
            }


            // 振休
            let furikyuu1: Furikyuu | null = null
            let furikyuu2: Furikyuu | null = null
            if (date === item.detail.furikyuu1_date && date === item.detail.furikyuu2_date) {
                // 一つの申請に複数の振休がある場合（MEDICA_AS-1202対応前の申請が対象）
                furikyuu1 = {
                    syukkin_date: null,
                    syukkin_furidetani_type: null,
                    start_time: null,
                    end_time: null,
                    kyuukei_time: null,
                    furikyuu1_date: item.detail.furikyuu1_date,
                    furikyuutani1_type: item.detail.furikyuutani1_type,
                    furikyuu2_date: null,
                    furikyuutani2_type: null,
                }

                furikyuu2 = {
                    syukkin_date: null,
                    syukkin_furidetani_type: null,
                    start_time: null,
                    end_time: null,
                    kyuukei_time: null,
                    furikyuu1_date: item.detail.furikyuu2_date,
                    furikyuutani1_type: item.detail.furikyuutani2_type,
                    furikyuu2_date: null,
                    furikyuutani2_type: null,
                }
            } else if (date === item.detail.furikyuu1_date) {
                // 振休取得日1のみある場合
                furikyuu1 = {
                    syukkin_date: null,
                    syukkin_furidetani_type: null,
                    start_time: null,
                    end_time: null,
                    kyuukei_time: null,
                    furikyuu1_date: item.detail.furikyuu1_date,
                    furikyuutani1_type: item.detail.furikyuutani1_type,
                    furikyuu2_date: null,
                    furikyuutani2_type: null,
                }
            } else if (date === item.detail.furikyuu2_date) {
                // 振休取得日2のみある場合（MEDICA_AS-1202対応前の申請が対象）
                furikyuu1 = {
                    syukkin_date: null,
                    syukkin_furidetani_type: null,
                    start_time: null,
                    end_time: null,
                    kyuukei_time: null,
                    furikyuu1_date: item.detail.furikyuu2_date,
                    furikyuutani1_type: item.detail.furikyuutani2_type,
                    furikyuu2_date: null,
                    furikyuutani2_type: null,
                }
            }


            // 取得した振休をダイアログ用の振休枠（shinseiStatus.furikyuu1または2）にセットする
            // item.detail.furikyuu1→2の順に、枠の1→2の空いてる方へ入れていく
            if (furikyuu1) {
                if (shinseiStatus.furikyuu1.furikyuutani1_type === null) {
                    shinseiStatus.furikyuu1 = furikyuu1
                    shinseiStatus.isFurikyuu = true
                } else if (shinseiStatus.furikyuu2.furikyuutani1_type === null) {
                    shinseiStatus.furikyuu2 = furikyuu1
                    shinseiStatus.isFurikyuu = true
                }
            }

            if (furikyuu2) {
                shinseiStatus.isFurikyuu = true
                if (shinseiStatus.furikyuu1.furikyuutani1_type === null) {
                    shinseiStatus.furikyuu1 = furikyuu2
                    shinseiStatus.isFurikyuu = true
                } else if (shinseiStatus.furikyuu2.furikyuutani1_type === null) {
                    shinseiStatus.furikyuu2 = furikyuu2
                    shinseiStatus.isFurikyuu = true
                }
            }


            // 申請に振出が含まれており、かつダイアログ用に振出がまだセットされていない場合はセットする
            if (item.detail.syukkin_date === date && shinseiStatus.furide.syukkin_furidetani_type === null) {
                shinseiStatus.furide = {
                    syukkin_date: item.detail.syukkin_date,
                    syukkin_furidetani_type: item.detail.syukkin_furidetani_type,
                    start_time: item.detail.start_time,
                    end_time: item.detail.end_time,
                    kyuukei_time: item.detail.kyuukei_time,
                    furikyuu1_date: null,
                    furikyuutani1_type: null,
                    furikyuu2_date: null,
                    furikyuutani2_type: null,
                }
                shinseiStatus.isFuride = true
            }

        })

        // 公休
        const tmpKoukyuu = shinseis.koukyuu.filter((item => item.syounin_status_type !== OptionConst.SYOUNIN_STATUS.CANCEL))
        const koukyuu = tmpKoukyuu.at(-1)
        if (koukyuu) {
            shinseiStatus.isKoukyuu = true
            shinseiStatus.koukyuu.koyou_keiyaku_id = koukyuu.koyou_keiyaku_id
            shinseiStatus.koukyuu.mst_syuugyou_basyo_id = koukyuu.mst_syuugyou_basyo_id
            shinseiStatus.koukyuu.koukyuu_date = koukyuu.detail.koukyuu_date
            shinseiStatus.koukyuu.koukyuu_tani_type = koukyuu.detail.koukyuu_tani_type
        }

        // 有休
        const tmpYuukyuu = shinseis.yuukyuu.filter((item => item.syounin_status_type !== OptionConst.SYOUNIN_STATUS.CANCEL))
        const yuukyuu = tmpYuukyuu.at(-1)
        if (yuukyuu) {
            shinseiStatus.isYuukyuu = true
            shinseiStatus.yuukyuu.start_date = yuukyuu.detail.start_date
            shinseiStatus.yuukyuu.yuukyuu_tani_type = yuukyuu.detail.yuukyuu_tani_type
            shinseiStatus.yuukyuu.yuukyuu_times = yuukyuu.detail.yuukyuu_times
        }

        // 忌引
        const tmpKibiki = shinseis.kibiki.filter((item => item.syounin_status_type !== OptionConst.SYOUNIN_STATUS.CANCEL))
        const kibiki = tmpKibiki.at(-1)
        if (kibiki) {
            shinseiStatus.isKibiki = true
            shinseiStatus.kibiki.kibiki_start_date = kibiki.detail.kibiki_start_date
            shinseiStatus.kibiki.kibiki_tani_type = kibiki.detail.kibiki_tani_type
            shinseiStatus.kibiki.kibiki_times = kibiki.detail.kibiki_times
            shinseiStatus.kibiki.kojin_name = kibiki.detail.kojin_name
            shinseiStatus.kibiki.kojin_kana = kibiki.detail.kojin_kana
            shinseiStatus.kibiki.tsuzukigara = kibiki.detail.tsuzukigara
            shinseiStatus.kibiki.sikyo_date = kibiki.detail.sikyo_date
        }

        // 欠勤
        const tmpKekkin = shinseis.kekkin.filter((item => item.syounin_status_type !== OptionConst.SYOUNIN_STATUS.CANCEL))
        const kekkin = tmpKekkin.at(-1)
        if (kekkin) {
            shinseiStatus.isKekkin = true
            shinseiStatus.kekkin.start_date = kekkin.detail.start_date
            shinseiStatus.kekkin.end_date = kekkin.detail.end_date
            shinseiStatus.kekkin.kekkin_days = 1
            // 通常であれば↓のロジックだが総務修正では１日単位でしか操作できないため↑のように固定値をいれる
            // shinseiStatus.kekkin.kekkin_days = kekkin.detail.kekkin_days
        }

    }

    return {
        form,
        shinseiStatus,
        initSyuuseiData,
        initShinseis
    }
}
